/**
 * Alert Component
 *
 * Alert/Banner component for displaying important messages.
 *
 * Usage:
 * <Alert variant="info" title="Info">Message</Alert>
 * <Alert variant="success">Success message</Alert>
 * <Alert variant="warning" dismissible onDismiss={() => {}}>Warning</Alert>
 */

import React, { ReactNode, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: ReactNode;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  const config = {
    info: {
      className:
        'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/60 text-blue-800 dark:text-blue-200',
      icon: <Info className="w-5 h-5" />,
    },
    success: {
      className:
        'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-200',
      icon: <CheckCircle2 className="w-5 h-5" />,
    },
    warning: {
      className:
        'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800/60 text-amber-800 dark:text-amber-200',
      icon: <AlertTriangle className="w-5 h-5" />,
    },
    error: {
      className:
        'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800/60 text-rose-800 dark:text-rose-200',
      icon: <XCircle className="w-5 h-5" />,
    },
  };

  const currentConfig = config[variant];

  return (
    <div className={`${currentConfig.className} border rounded-xl p-4 ${className}`} role="alert">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">{icon || currentConfig.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && <h4 className="font-semibold mb-1 text-sm">{title}</h4>}
          <div className="text-sm leading-relaxed">{children}</div>
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-current opacity-60 hover:opacity-100 transition-opacity p-0.5 rounded-lg"
            aria-label="Dispensar"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
